/**
 * Thin AVOWAL API client.
 *
 * In production the SPA is same-origin with the API (avowal.ai/api/* via CloudFront).
 * In dev, Vite proxies /api/* to the live API Gateway (see vite.config.ts).
 *
 * `submit()` posts the form, returns a discriminated result.
 */

export type FormKind = 'waitlist' | 'intake';

export type SubmitResult =
  | { ok: true; message: string }
  | { ok: false; error: string };

export async function submit(kind: FormKind, data: Record<string, unknown>): Promise<SubmitResult> {
  try {
    const res = await fetch(`/api/${kind}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...data, kind, source: 'avowal.ai', ts: new Date().toISOString() }),
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok || body.ok === false) {
      return { ok: false, error: body.error ?? `HTTP ${res.status}` };
    }
    return {
      ok: true,
      message:
        body.message ??
        (kind === 'intake'
          ? 'Received. You will hear back within one business day.'
          : 'You are on the list. Welcome.'),
    };
  } catch (err) {
    return { ok: false, error: (err as Error).message };
  }
}

export interface LeadFields {
  email: string;
  jurisdiction?: string;
  matterType?: string;
  summary?: string;
}

/**
 * Capture an intake into the real triage pipeline (avowal-api POST /leads).
 *
 * `submitted: true` graduates a complete form to a `new` intake that lands in
 * the staff queue (and alerts staff). `submitted: false` persists whatever the
 * prospect has entered as a `draft` so an abandoned form survives as a lead —
 * empty fields are dropped so a partial save never clobbers captured detail.
 */
export async function captureLead(fields: LeadFields, submitted: boolean): Promise<SubmitResult> {
  try {
    const payload: Record<string, unknown> = { email: fields.email.trim(), source: 'avowal.ai', submitted };
    const j = fields.jurisdiction?.trim();
    const m = fields.matterType?.trim();
    const s = fields.summary?.trim();
    if (j) payload.jurisdiction = j;
    if (m) payload.matterType = m;
    if (s) payload.summary = s;

    const res = await fetch('/api/leads', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok || body.ok === false) {
      return { ok: false, error: body.error ?? `HTTP ${res.status}` };
    }
    return {
      ok: true,
      message:
        body.status === 'new'
          ? 'Received. ELARIA reviews intake first — you\'ll hear back within one business day.'
          : 'Saved. Pick up where you left off whenever you\'re ready.',
    };
  } catch (err) {
    return { ok: false, error: (err as Error).message };
  }
}
